import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CalendarCheck, CalendarClock, Play, CheckCircle2, XCircle, RefreshCcw, UserCog, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_COLORS = {
  pending_client_selection: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-slate-100 text-slate-600",
  canceled: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  pending_client_selection: "Awaiting Client",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  canceled: "Canceled",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ClientScheduleAdmin({ clientId, team = [], onChange, showClientColumn = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      const { data } = await api.get("/admin/schedules", { params });
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const doAction = async (path, sid, ok, patch) => {
    try {
      await api.post(`/admin/schedules/${sid}/${path}`, patch || {});
      toast.success(ok);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="admin-schedule-list">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><CalendarClock size={18} /></div>
        <div>
          <h3 className="font-display font-bold text-lg">Scheduling</h3>
          <p className="text-xs text-slate-500">Proposed, confirmed, and completed processing sessions.</p>
        </div>
      </div>
      {loading && <p className="text-sm text-slate-400">Loading…</p>}
      {!loading && items.length === 0 && <p className="text-sm text-slate-400" data-testid="admin-schedule-empty">No schedules yet.</p>}
      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-100 p-3 sm:p-4 bg-white" data-testid={`schedule-row-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${STATUS_COLORS[s.status]} border-0 uppercase text-[10px] tracking-widest`}>{STATUS_LABEL[s.status] || s.status}</Badge>
                  <span className="font-semibold text-slate-900">{s.operation_type}</span>
                  {showClientColumn && s.client_name && (
                    <button className="text-xs text-sky-600 hover:underline" onClick={() => nav(`/admin/clients/${s.client_id}`)} data-testid={`schedule-client-link-${s.id}`}>
                      {s.client_name}
                    </button>
                  )}
                </div>
                <div className="text-sm text-slate-700 mt-1">
                  {s.scheduled_start ? (
                    <span><CalendarCheck size={14} className="inline -mt-0.5 mr-1 text-emerald-600" /> {fmt(s.scheduled_start)}</span>
                  ) : (
                    <span className="text-slate-500"><CalendarClock size={14} className="inline -mt-0.5 mr-1" /> {s.proposed_options?.length || 0} option(s) awaiting client</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Assignee: <span className="font-semibold text-slate-700">{s.assigned_user_name || "—"}</span>
                  {s.duration_minutes && <> · {s.duration_minutes} min</>}
                  {s.requires_client_on_call && <> · <span className="text-amber-700 font-semibold">Client on call</span></>}
                </div>
                {s.status === "pending_client_selection" && s.proposed_options?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.proposed_options.map((o) => (
                      <span key={o.id} className="text-[11px] px-2 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-100">{fmt(o.start_iso)}</span>
                    ))}
                  </div>
                )}
                {s.notes && <p className="text-xs text-slate-500 mt-2 italic">"{s.notes}"</p>}
              </div>
              <div className="flex flex-wrap gap-1">
                {s.status === "confirmed" && (
                  <Button size="sm" onClick={() => doAction("start", s.id, "Started")} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid={`start-${s.id}`}>
                    <Play size={12} className="mr-1" /> Start Now
                  </Button>
                )}
                {(s.status === "in_progress" || s.status === "confirmed") && (
                  <Button size="sm" onClick={() => doAction("complete", s.id, "Completed")} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`complete-${s.id}`}>
                    <CheckCircle2 size={12} className="mr-1" /> Complete
                  </Button>
                )}
                {(s.status === "confirmed" || s.status === "in_progress" || s.status === "pending_client_selection") && (
                  <RescheduleButton sched={s} onDone={() => { load(); onChange && onChange(); }} />
                )}
                {(s.status === "confirmed" || s.status === "in_progress") && team.length > 0 && (
                  <ReassignButton sched={s} team={team} onDone={() => { load(); onChange && onChange(); }} />
                )}
                {["pending_client_selection", "confirmed", "in_progress"].includes(s.status) && (
                  <Button size="sm" variant="outline" onClick={() => doAction("cancel", s.id, "Canceled")} className="text-red-600 border-red-200" data-testid={`cancel-${s.id}`}>
                    <XCircle size={12} className="mr-1" /> Cancel
                  </Button>
                )}
                {!clientId && s.client_id && (
                  <Button size="sm" variant="ghost" onClick={() => nav(`/admin/clients/${s.client_id}`)} data-testid={`open-client-${s.id}`}>
                    <ExternalLink size={12} className="mr-1" /> Open Client
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RescheduleButton({ sched, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(sched.scheduled_start ? sched.scheduled_start.slice(0, 10) : "");
  const [time, setTime] = useState(sched.scheduled_start ? new Date(sched.scheduled_start).toISOString().slice(11, 16) : "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!date || !time) return toast.error("Pick date & time");
    setBusy(true);
    try {
      const iso = new Date(`${date}T${time}:00`).toISOString();
      await api.post(`/admin/schedules/${sched.id}/reschedule`, { start: iso });
      toast.success("Rescheduled");
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to reschedule");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`reschedule-${sched.id}`}><RefreshCcw size={12} className="mr-1" /> Reschedule</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Reschedule</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date" /></div>
          <div><Label>Time</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="reschedule-time" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="reschedule-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignButton({ sched, team, onDone }) {
  const [open, setOpen] = useState(false);
  const [uid, setUid] = useState(sched.assigned_user_id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!uid) return toast.error("Pick a team member");
    setBusy(true);
    try {
      await api.post(`/admin/schedules/${sched.id}/reassign`, { assigned_user_id: uid });
      toast.success("Reassigned");
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to reassign");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`reassign-${sched.id}`}><UserCog size={12} className="mr-1" /> Reassign</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Reassign specialist</DialogTitle></DialogHeader>
        <Select value={uid} onValueChange={setUid}>
          <SelectTrigger data-testid="reassign-select"><SelectValue placeholder="Pick team member" /></SelectTrigger>
          <SelectContent>
            {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="reassign-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
